import React from "react"
import {
  CheckCircle,
  Clock,
  Facebook,
  Github,
  Linkedin,
  Mail,
  Shield,
  Star,
  Twitter,
  Users
} from "lucide-react"

export default function Footer() {
  return (
    <footer className="py-16 bg-slate-950 relative z-10 border-t border-slate-800">
      <div className="container mx-auto px-4">
        {/* Main Footer Content */}
        <div className="grid md:grid-cols-5 gap-8 mb-12">
          {/* Brand Section */}
          <div className="md:col-span-1">
            <h3 className="text-xl font-bold text-white mb-4">VEDAVIVI</h3>
            <p className="text-sm text-gray-400 mb-4">
              Empowering your life, one task at a time.
            </p>

            {/* Trust Signals */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-gray-300">
                <Users className="h-4 w-4 text-brand-teal" aria-hidden="true" />
                <span>10,000+ active users</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-300">
                <Clock className="h-4 w-4 text-brand-teal" aria-hidden="true" />
                <span>99.9% uptime guaranteed</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-300">
                <Star className="h-4 w-4 text-brand-teal" aria-hidden="true" />
                <span>4.9/5 user satisfaction</span>
              </div>
            </div>
          </div>

          {/* Product Section */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-4">Product</h4>
            <ul className="space-y-3 text-sm">
              <li>
                <a href="#features" className="text-gray-400 hover:text-brand-teal transition-colors">
                  Features
                </a>
              </li>
              <li>
                <a href="#pricing" className="text-gray-400 hover:text-brand-teal transition-colors">
                  Pricing
                </a>
              </li>
              <li>
                <a href="/demo" className="text-gray-400 hover:text-brand-teal transition-colors">
                  Demo
                </a>
              </li>
              <li>
                <a href="/integrations" className="text-gray-400 hover:text-brand-teal transition-colors">
                  Integrations
                </a>
              </li>
              <li>
                <a href="/api" className="text-gray-400 hover:text-brand-teal transition-colors">
                  API
                </a>
              </li>
            </ul>
          </div>

          {/* Company Section */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-4">Company</h4>
            <ul className="space-y-3 text-sm">
              <li>
                <a href="/about" className="text-gray-400 hover:text-brand-teal transition-colors">
                  About Us
                </a>
              </li>
              <li>
                <a href="/careers" className="text-gray-400 hover:text-brand-teal transition-colors">
                  Careers
                </a>
              </li>
              <li>
                <a href="/press" className="text-gray-400 hover:text-brand-teal transition-colors">
                  Press
                </a>
              </li>
              <li>
                <a href="/contact" className="text-gray-400 hover:text-brand-teal transition-colors">
                  Contact
                </a>
              </li>
              <li>
                <a href="/blog" className="text-gray-400 hover:text-brand-teal transition-colors">
                  Blog
                </a>
              </li>
            </ul>
          </div>

          {/* Support Section */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-4">Support</h4>
            <ul className="space-y-3 text-sm">
              <li>
                <a href="/help" className="text-gray-400 hover:text-brand-teal transition-colors">
                  Help Center
                </a>
              </li>
              <li>
                <a href="/docs" className="text-gray-400 hover:text-brand-teal transition-colors">
                  Documentation
                </a>
              </li>
              <li>
                <a href="/status" className="text-gray-400 hover:text-brand-teal transition-colors">
                  Status Page
                </a>
              </li>
              <li>
                <a href="/support" className="text-gray-400 hover:text-brand-teal transition-colors">
                  Contact Support
                </a>
              </li>
              <li>
                <a href="/community" className="text-gray-400 hover:text-brand-teal transition-colors">
                  Community
                </a>
              </li>
            </ul>
          </div>

          {/* Contact & Legal Section */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-4">Legal & Contact</h4>
            <ul className="space-y-3 text-sm mb-6">
              <li>
                <a href="/privacy" className="text-gray-400 hover:text-brand-teal transition-colors">
                  Privacy Policy
                </a>
              </li>
              <li>
                <a href="/terms" className="text-gray-400 hover:text-brand-teal transition-colors">
                  Terms of Service
                </a>
              </li>
              <li>
                <a href="/security" className="text-gray-400 hover:text-brand-teal transition-colors">
                  Security
                </a>
              </li>
              <li>
                <a href="/gdpr" className="text-gray-400 hover:text-brand-teal transition-colors">
                  GDPR Compliance
                </a>
              </li>
            </ul>

            {/* Contact Information */}
            <div className="space-y-2 text-sm text-gray-400">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-brand-teal" aria-hidden="true" />
                <a href="mailto:support@vedavivi.com" className="hover:text-brand-teal transition-colors">
                  support@vedavivi.com
                </a>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-brand-teal" aria-hidden="true" />
                <span>24/7 Support</span>
              </div>
            </div>
          </div>
        </div>

        {/* Security Badges & Trust Signals */}
        <div className="border-t border-slate-800 pt-8 mb-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            {/* Security Badges */}
            <div className="flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-2 text-sm text-gray-300">
                <Shield className="h-5 w-5 text-brand-teal" aria-hidden="true" />
                <span>SOC 2 Compliant</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-300">
                <CheckCircle className="h-5 w-5 text-brand-teal" aria-hidden="true" />
                <span>GDPR Ready</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-300">
                <Shield className="h-5 w-5 text-brand-teal" aria-hidden="true" />
                <span>ISO 27001</span>
              </div>
            </div>

            {/* Additional Trust Elements */}
            <div className="text-center md:text-right">
              <p className="text-sm text-gray-300 mb-1">
                Trusted by teams at
              </p>
              <p className="text-xs text-gray-400">
                Fortune 500 companies worldwide
              </p>
            </div>
          </div>
        </div>

        {/* Social Media & Copyright */}
        <div className="border-t border-slate-800 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            {/* Social Media Links */}
            <div className="flex items-center space-x-6">
              <a
                href="https://facebook.com/vedavivi"
                className="text-gray-400 hover:text-brand-teal transition-colors"
                aria-label="Follow us on Facebook"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Facebook size={20} />
              </a>
              <a
                href="https://twitter.com/vedavivi"
                className="text-gray-400 hover:text-brand-teal transition-colors"
                aria-label="Follow us on Twitter"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Twitter size={20} />
              </a>
              <a
                href="https://linkedin.com/company/vedavivi"
                className="text-gray-400 hover:text-brand-teal transition-colors"
                aria-label="Follow us on LinkedIn"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Linkedin size={20} />
              </a>
              <a
                href="https://github.com/vedavivi"
                className="text-gray-400 hover:text-brand-teal transition-colors"
                aria-label="Follow us on GitHub"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Github size={20} />
              </a>
            </div>

            {/* Copyright */}
            <div className="text-center md:text-right">
              <p className="text-xs text-gray-400">
                &copy; {new Date().getFullYear()} VEDAVIVI. All rights reserved.
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Made with ❤️ for productivity enthusiasts
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}
